import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AuthLayout } from '@app/layouts/AuthLayout';
import { MainLayout } from '@app/layouts/MainLayout';
import { ProtectedRoute } from './ProtectedRoute';
import { RouteFallback } from './RouteFallback';

// Lazy views — each one is its own chunk.
const LoginView = lazy(() =>
  import('@features/auth/views/LoginView').then((m) => ({ default: m.LoginView })),
);
const DashboardView = lazy(() =>
  import('@features/dashboard/views/DashboardView').then((m) => ({ default: m.DashboardView })),
);
const InboxView = lazy(() =>
  import('@features/inbox/views/InboxView').then((m) => ({ default: m.InboxView })),
);
const IntegrationsView = lazy(() =>
  import('@features/channels/views/IntegrationsView').then((m) => ({
    default: m.IntegrationsView,
  })),
);
const NotFoundView = lazy(() =>
  import('@app/router/NotFoundView').then((m) => ({ default: m.NotFoundView })),
);
const UiKitShowcaseView = lazy(() =>
  import('@app/router/UiKitShowcaseView').then((m) => ({ default: m.UiKitShowcaseView })),
);
const PricingCalculatorView = lazy(() =>
  import('@features/pricing/views/PricingCalculatorView').then((m) => ({
    default: m.PricingCalculatorView,
  })),
);
const KnowledgeView = lazy(() =>
  import('@features/knowledge-base/views/KnowledgeView').then((m) => ({
    default: m.KnowledgeView,
  })),
);
const TokensCostsView = lazy(() =>
  import('@features/tokens-costs/views/TokensCostsView').then((m) => ({
    default: m.TokensCostsView,
  })),
);
const HandoverRulesView = lazy(() =>
  import('@features/automations/views/HandoverRulesView').then((m) => ({
    default: m.HandoverRulesView,
  })),
);
const BlocklistView = lazy(() =>
  import('@features/automations/views/BlocklistView').then((m) => ({
    default: m.BlocklistView,
  })),
);
const IdealResponsesView = lazy(() =>
  import('@features/feedback/views/IdealResponsesView').then((m) => ({
    default: m.IdealResponsesView,
  })),
);
const BillingView = lazy(() =>
  import('@features/billing/views/BillingView').then((m) => ({
    default: m.BillingView,
  })),
);
const ClientesView = lazy(() =>
  import('@features/customers/views/ClientesView').then((m) => ({
    default: m.ClientesView,
  })),
);
const AgentesIAView = lazy(() =>
  import('@features/ai-agents/views/AgentesIAView').then((m) => ({
    default: m.AgentesIAView,
  })),
);
const ReportesView = lazy(() =>
  import('@features/reports/views/ReportesView').then((m) => ({
    default: m.ReportesView,
  })),
);
const AjustesView = lazy(() =>
  import('@features/settings/views/AjustesView').then((m) => ({
    default: m.AjustesView,
  })),
);
const ChannelSetupView = lazy(() =>
  import('@features/channels/views/ChannelSetupView').then((m) => ({
    default: m.ChannelSetupView,
  })),
);

export function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginView />} />
          <Route path="/pricing" element={<PricingCalculatorView />} />
        </Route>

        {/* Dev-only: UI Kit showcase */}
        {import.meta.env.DEV && (
          <Route element={<MainLayout />}>
            <Route path="/_kit" element={<UiKitShowcaseView />} />
          </Route>
        )}

        {/* Protected */}
        <Route element={<ProtectedRoute />}>
          <Route element={<MainLayout />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardView />} />
            <Route path="/inbox" element={<InboxView />} />
            <Route path="/integrations" element={<IntegrationsView />} />
            <Route path="/knowledge" element={<KnowledgeView />} />
            <Route path="/tokens-costs" element={<TokensCostsView />} />
            <Route path="/automations" element={<HandoverRulesView />} />
            <Route path="/blocklist" element={<BlocklistView />} />
            <Route path="/ideal-responses" element={<IdealResponsesView />} />
            <Route path="/billing" element={<BillingView />} />
            <Route path="/customers" element={<ClientesView />} />
            <Route path="/ai-agents" element={<AgentesIAView />} />
            <Route path="/reports" element={<ReportesView />} />
            <Route path="/settings" element={<AjustesView />} />
            <Route path="/integrations/setup/:kind" element={<ChannelSetupView />} />
          </Route>
        </Route>

        <Route path="*" element={<NotFoundView />} />
      </Routes>
    </Suspense>
  );
}
